"use client";
import { useEffect, useMemo, useState } from "react";
import { brand } from "@/lib/theme";
import apiClient from "@/lib/apiClient";

interface FirstWebsiteFormProps {
  userEmail?: string;
  isFirst?: boolean;
}

export default function FirstWebsiteForm({ userEmail, isFirst = true }: FirstWebsiteFormProps) {
  const [website, setWebsite] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Prefill with www.{domain} from email (only once on mount)
  useEffect(() => {
    if (userEmail) {
      const parts = userEmail.split("@");
      if (parts.length === 2) {
        const domain = parts[1];
        setWebsite(`www.${domain}`);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail]);

  const canSubmit = useMemo(() => {
    const trimmed = website.trim();
    // Basic validation: must have at least a dot
    return trimmed.length > 0 && trimmed.includes(".") && !loading;
  }, [website, loading]);

  const onSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Normalize the website URL
      let normalizedUrl = website.trim();
      if (!normalizedUrl.startsWith("http://") && !normalizedUrl.startsWith("https://")) {
        normalizedUrl = `https://${normalizedUrl}`;
      }

      // Create the domain via API
      const response = await apiClient.post("/v1/domains", {
        url: normalizedUrl,
      });

      // Extract the domain ID from the response
      const domainId = response?.data?.id || response?.data?.data?.id;

      // Redirect to the domain details page
      if (domainId) {
        window.location.href = `/domain/${domainId}`;
      } else {
        // Fallback to home if no ID is returned
        window.location.href = "/";
      }
    } catch (e: unknown) {
      const msg =
        typeof e === "object" && e && "message" in e && typeof (e as Record<string, unknown>).message === "string"
          ? ((e as Record<string, unknown>).message as string)
          : "Failed to add website. Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 16,
        padding: isFirst ? 20 : 0,
        border: isFirst ? `1px solid ${brand.border}` : "none",
        borderRadius: isFirst ? 12 : 0,
        background: isFirst ? "#fff" : "transparent",
        boxShadow: isFirst ? "0 2px 8px rgba(0,0,0,0.06)" : "none",
        minWidth: isFirst ? 400 : "auto",
        maxWidth: isFirst ? 500 : "none",
        width: isFirst ? "auto" : "100%",
      }}
    >
      {isFirst && (
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>Add your first website</h2>
          <p style={{ margin: "8px 0 0", fontSize: 14, color: brand.muted }}>
            Enter your website URL to get started with Refinea
          </p>
        </div>
      )}

      <label style={{ display: "grid", gap: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: brand.text }}>Website URL</span>
        <input
          type="text"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          placeholder="www.example.com"
          style={{
            padding: "12px 14px",
            borderRadius: 8,
            border: `1px solid ${brand.border}`,
            background: brand.subtle,
            color: brand.text,
            fontSize: 14,
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && canSubmit) {
              onSubmit();
            }
          }}
        />
      </label>

      {error && (
        <div style={{ color: "#DC2626", fontSize: 13, fontWeight: 500 }}>
          {error}
        </div>
      )}

      <button
        disabled={!canSubmit}
        onClick={onSubmit}
        style={{
          padding: "12px 16px",
          borderRadius: 8,
          border: "none",
          background: canSubmit ? brand.color : brand.border,
          color: "#fff",
          fontWeight: 700,
          fontSize: 14,
          cursor: canSubmit ? "pointer" : "not-allowed",
          transition: "background 150ms",
        }}
        onMouseEnter={(e) => {
          if (canSubmit) {
            e.currentTarget.style.background = brand.color;
          }
        }}
        onMouseLeave={(e) => {
          if (canSubmit) {
            e.currentTarget.style.background = brand.color;
          }
        }}
      >
        {loading ? "Adding..." : "Add Website"}
      </button>
    </div>
  );
}
